import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DatabasesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatabasesDisplayGridRowDataFragment';
import {
  FlowUpstreamDatabasesFilteredByFlowOutputFieldQuery,
  FlowUpstreamDatabasesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamDatabasesConnection_nodes,
  FlowUpstreamDatabasesFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamDatabasesFilteredByFlowOutputFieldQuery';
import {
  FlowUpstreamDatabasesQuery,
  FlowUpstreamDatabasesQuery_flows_upstreamDatabasesConnection_nodes,
  FlowUpstreamDatabasesQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamDatabasesQuery';
import { DatabaseSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  FlowUpstreamDatabasesFilteredByFlowOutputFieldQueryGql,
} from '../../queries/FlowUpstreamDatabasesFilteredByFlowOutputFieldQueryGql';
import { FlowUpstreamDatabasesQueryGql } from '../../queries/FlowUpstreamDatabasesQueryGql';
import { getRowDataWithMaxCountsForDatabase } from '../database/functions/DatabaseRowDataFunctions';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import { DatabasesDisplayGrid } from '../sharedDisplayGrid/DatabasesDisplayGrid';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';

type QueryVariables = FlowUpstreamDatabasesQueryVariables;
type QueryResults = FlowUpstreamDatabasesQuery;
type QueryResultListNode = FlowUpstreamDatabasesQuery_flows_upstreamDatabasesConnection_nodes;
type GridRowData = DatabasesDisplayGridRowData;

type QueryVariablesWithFilter = FlowUpstreamDatabasesFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowUpstreamDatabasesFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowUpstreamDatabasesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamDatabasesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.upstreamDatabasesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.upstreamDatabasesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field;
}

function getRowDataForDatabase(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResults,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResult(resultData));
}

function getRowDataForDatabaseForFilteredQuery(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResultForFilteredQuery(resultData));
}

export type FlowUpstreamDatabasesQueryGridProps =
  GridSortProps<DatabasesValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowUpstreamDatabasesQueryGrid(props: FlowUpstreamDatabasesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamDatabasesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatabasesLineageGridMessage();

  return props.filteredFlowOutputFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={FlowUpstreamDatabasesFilteredByFlowOutputFieldQueryGql}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
        orderBy: props.orderBy as DatabaseSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatabaseForFilteredQuery}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={FlowUpstreamDatabasesQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as DatabaseSortOrder,
          publishedConnectionsLineageEnabled
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatabase}
      >
        {({ rowStore }) =>
          <DatabasesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
      </PaginatedQueryWrapper>
    );
}
